import { useEffect, useState } from "react";
import { Offcanvas, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from "react-redux";
import { getActiveUsers, getRoles } from "../../slices/userReducer";
import { BiFontSize } from "react-icons/bi";
import { validateField, validateAllFields, createClient, listCategoryTypes } from './ClientHelper';
import toast from 'react-hot-toast';
import { FaPlus, FaTrash } from "react-icons/fa";
import instance from "../../services/baseService";
import { truncateText, truncateTextOnly } from "../../utils";
import DynamicCreateModal from "../common/DynamicCreateModal";
import { extractErrorMessage } from "../../constants/common";

function CreateNewClient(props) {
    const {
        handleSubmit,
        fields,
        errors: propErrors,
        show,
        onHideFunc,
        values,
        editClientPermission = null
    } = props;

    const {
        activeUsers: userData,
        roles: rolesData,
    } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const [currentModalType, setCurrentModalType] = useState(null);
    const [data, setData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [apiSuccess, setApiSuccess] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingItem, setCreatingItem] = useState(false);
    // Hierarchical role states
    const [categoryTypes, setCategoryTypes] = useState([]);
    const [loadingCategoryTypes, setLoadingCategoryTypes] = useState(false);
    const [roleOptions, setRoleOptions] = useState({});
    const [loadingRoles, setLoadingRoles] = useState({});
    const [roleFields, setRoleFields] = useState([{
        id: 0,
        value: '',
        selectedRoleId: null,
        error: false
    }]);
    const [nextRoleId, setNextRoleId] = useState(1);

    useEffect(() => {
        setData(values);
        setValidationErrors({});
        setTouched({});
        setApiError('');
        setApiSuccess('');
        if (show) {
            dispatch(getActiveUsers({}));
            dispatch(getRoles());
            // Initialize first level roles when component shows
            fetchRoles(0);
            fetchCategoryTypes();
        }
    }, [values, show, dispatch]);

    // Combine validation errors with prop errors
    const allErrors = { ...validationErrors, ...propErrors };

    // API call to fetch roles for hierarchical selection
    const fetchRoles = async (roleIndex, parentRoleId = null) => {
        try {
            setLoadingRoles(prev => ({
                ...prev,
                [roleIndex]: true
            }));

            const endpoint = parentRoleId
                ? `/api/v1/rbac/approval/roles/list/${parentRoleId}`
                : `/api/v1/rbac/approval/roles/list`;

            const response = await instance.get(endpoint);

            if (response.data?.type === 'success') {
                setRoleOptions(prev => ({
                    ...prev,
                    [roleIndex]: response.data.data
                }));
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            // Fallback to Redux roles if API fails
            setRoleOptions(prev => ({
                ...prev,
                [roleIndex]: rolesData || []
            }));
        } finally {
            setLoadingRoles(prev => ({
                ...prev,
                [roleIndex]: false
            }));
        }
    };

    const fetchCategoryTypes = async () => {
        setLoadingCategoryTypes(true);
        try {
            const response = await listCategoryTypes();
            setCategoryTypes(response.data);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setLoadingCategoryTypes(false);
        }
    };

    const handleChange = (e, field) => {
        const fieldData = { ...data };
        let fieldValue;

        if (e.target.type === 'checkbox') {
            fieldValue = e.target.checked;
        } else {
            fieldValue = e.target.value;
        }

        fieldData[field] = fieldValue;
        setData(fieldData);

        // Mark field as touched
        setTouched(prev => ({ ...prev, [field]: true }));

        // Clear API messages when user starts typing
        if (apiError) setApiError('');
        if (apiSuccess) setApiSuccess('');

        // Validate field in real-time if it has been touched
        if (fields[field]) {
            const error = validateField(field, fieldValue, fields[field]);
            setValidationErrors(prev => ({
                ...prev,
                [field]: error
            }));
        }
    };

    const handleBlur = (field) => {
        // Mark field as touched on blur
        setTouched(prev => ({ ...prev, [field]: true }));

        // Validate field on blur
        if (fields[field]) {
            const error = validateField(field, data[field], fields[field]);
            setValidationErrors(prev => ({
                ...prev,
                [field]: error
            }));
        }
    };

    const handleRoleChange = async (e, roleId) => {
        const selectedValue = e.target.value;
        const roleIndex = roleFields.findIndex(role => role.id === roleId);
        const availableRoles = roleOptions[roleId] || [];
        const selectedRole = availableRoles.find(role => role.name === selectedValue);

        // Update role fields
        setRoleFields(prev => prev.map(role =>
            role.id === roleId ? {
                ...role,
                value: selectedValue,
                selectedRoleId: selectedRole ? selectedRole.id : null,
                error: false
            } : role
        ));

        // Clear options for subsequent roles when current selection changes
        const currentRoleIndex = roleFields.findIndex(role => role.id === roleId);

        // Remove role options for roles that come after the current one
        setRoleOptions(prev => {
            const newOptions = { ...prev };
            Object.keys(newOptions).forEach(key => {
                const keyIndex = roleFields.findIndex(role => role.id === parseInt(key));
                if (keyIndex > currentRoleIndex) {
                    delete newOptions[key];
                }
            });
            return newOptions;
        });

        // Clear subsequent role selections
        setRoleFields(prev => prev.map((role, index) =>
            index > currentRoleIndex ? {
                ...role,
                value: '',
                selectedRoleId: null,
                error: false
            } : role
        ));

        // ✅ NEW: Auto-fetch options for the immediate next role if it exists
        const nextRoleIndex = currentRoleIndex + 1;
        const nextRole = roleFields[nextRoleIndex];

        if (nextRole && selectedRole) {
            await fetchRoles(nextRole.id, selectedRole.id);
        }

        // Update the actual form data for approver_levels with role IDs
        const updatedRoles = roleFields.map((role, index) => {
            if (role.id === roleId) {
                return {
                    ...role,
                    value: selectedValue,
                    selectedRoleId: selectedRole ? selectedRole.id : null
                };
            } else if (index > currentRoleIndex) {
                return {
                    ...role,
                    value: '',
                    selectedRoleId: null
                };
            }
            return role;
        });

        const selectedRoleIds = updatedRoles
            .filter(role => role.selectedRoleId)
            .map(role => role.selectedRoleId);

        setData(prev => ({ ...prev, approver_levels: selectedRoleIds }));
        if (selectedValue && fields['approver_levels']) {
            const error = validateField('approver_levels', selectedRoleIds, fields['approver_levels']);
            setValidationErrors(prev => ({
                ...prev,
                approver_levels: error
            }));

            // Mark field as touched
            setTouched(prev => ({ ...prev, approver_levels: true }));
        }
    };

    const handleAddRole = (currentRoleId) => {
        const currentRole = roleFields.find(role => role.id === currentRoleId);

        // Validate current selection
        if (!currentRole.value || !currentRole.selectedRoleId) {
            setRoleFields(prev => prev.map(role =>
                role.id === currentRoleId ? { ...role, error: true } : role
            ));
            return;
        }

        // Clear error and add new role
        const newRoleId = nextRoleId;
        setRoleFields(prev => [
            ...prev.map(role =>
                role.id === currentRoleId ? { ...role, error: false } : role
            ),
            { id: newRoleId, value: '', selectedRoleId: null, error: false }
        ]);

        setNextRoleId(prev => prev + 1);

        // Fetch roles for the new dropdown based on current selection
        fetchRoles(newRoleId, currentRole.selectedRoleId);
    };

    const handleDeleteRole = (roleId) => {
        setRoleFields(prev => prev.filter(role => role.id !== roleId));

        // Clean up role options for deleted role
        setRoleOptions(prev => {
            const newOptions = { ...prev };
            delete newOptions[roleId];
            return newOptions;
        });

        // Update the actual form data for approver_levels with role IDs
        const updatedRoles = roleFields.filter(role => role.id !== roleId);
        const selectedRoleIds = updatedRoles
            .filter(role => role.selectedRoleId)
            .map(role => role.selectedRoleId);

        setData(prev => ({ ...prev, approver_levels: selectedRoleIds }));
    };

    const handleCancel = () => {
        if (typeof onHideFunc === 'function') {
            onHideFunc();
        }
        setData(values);
        setValidationErrors({});
        setTouched({});
        setRoleFields([{ id: 0, value: "", selectedRoleId: null, error: false }]);
        setRoleOptions({});
        setLoadingRoles({});
        setNextRoleId(1);
        setApiError('');
        setApiSuccess('');
        setIsSubmitting(false);
    };

    const handleCheckboxGroupChange = (e, field, value) => {
        const updatedValues = data[field] ? [...data[field]] : [];

        if (e.target.checked) {
            updatedValues.push({
                id: value,
                name: fields[field]['options'].find(opt => opt.value === value).label
            });
        } else {
            const index = updatedValues.findIndex(item => item.id === value);
            if (index > -1) {
                updatedValues.splice(index, 1);
            }
        }

        const newData = { ...data, [field]: updatedValues };
        setData(newData);

        // Validate the field
        if (fields[field]) {
            const error = validateField(field, updatedValues, fields[field]);
            setValidationErrors(prev => ({
                ...prev,
                [field]: error
            }));
        }
    };

    // Transform form data to API format
    const transformDataForApi = (formData) => {
        const apiData = {
            client_name: formData.client_name || '',
            trn: formData.trn || '',
            trade_license_no: formData.trade_license_no || '',
            email: formData.email || '',
            from_email: formData.from_email || '',
            phone: formData.phone || null,
            city: formData.city || null,
            area: formData.area || null,
            country: formData.country || null,
            emirates: formData.emirates || null,
            client_class: formData.client_class || null,
            poc_mail: formData.poc_mail || null,
            assignee_id: formData.assignee_id || null,
            responsible_person_id: formData.responsible_person_id || null,
            category: formData.category || null,
            approver_levels: formData.approver_levels || [],
            is_active: formData.is_active !== undefined ? formData.is_active : false,
        };

        // Remove null/empty values to keep API payload clean
        Object.keys(apiData).forEach(key => {
            if (apiData[key] === null || apiData[key] === '') {
                delete apiData[key];
            }
        });

        return apiData;
    };

    // Enhanced form submission with API integration
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Clear previous messages
        setApiError('');
        setApiSuccess('');

        // Validate all fields
        const errors = validateAllFields(data, fields);

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            // Mark all fields as touched to show errors
            const allTouched = {};
            Object.keys(fields).forEach(field => {
                allTouched[field] = true;
            });
            setTouched(allTouched);
            return;
        }

        // Clear validation errors and proceed with API call
        setValidationErrors({});
        setIsSubmitting(true);

        try {
            const apiData = transformDataForApi(data);
            const response = await createClient(apiData);

            if (response.type === 'success') {
                setApiSuccess(response.message);
                toast.success(response?.message || 'Created client!');
                if (handleSubmit && typeof handleSubmit === 'function') {
                    handleSubmit(e, false, response.data);
                }

                // Auto-close the modal after successful creation
                setTimeout(() => {
                    handleCancel();
                }, 1500);
            } else if (response.type === 'validation_error') {
                // Handle validation errors from API response
                if (response.data && typeof response.data === 'object') {
                    // Set field-specific validation errors
                    setValidationErrors(response.data);

                    // Extract error messages for API error display
                    const errors = [];
                    Object.keys(response.data).forEach(field => {
                        if (response.data[field] && Array.isArray(response.data[field])) {
                            errors.push(...response.data[field]);
                        }
                    });

                    if (errors.length > 0) {
                        setApiError(errors.join(', '));
                    } else {
                        setApiError('Please check the form for errors.');
                    }
                } else {
                    setApiError(response.message || 'Validation error occurred.');
                }
            } else {
                // Handle other error types
                setApiError(response.message || 'An error occurred while saving the client.');
            }
        } catch (error) {
            console.error('API Error:', error);
            let errorMessage = 'An error occurred while saving the client.';

            if (error.response && error.response.data) {
                if (error.response.data.type === 'validation_error') {
                    // Handle validation errors from catch block
                    if (error.response.data.data && typeof error.response.data.data === 'object') {
                        setValidationErrors(error.response.data.data);

                        const errors = [];
                        Object.keys(error.response.data.data).forEach(field => {
                            if (error.response.data.data[field] && Array.isArray(error.response.data.data[field])) {
                                errors.push(...error.response.data.data[field]);
                            }
                        });

                        errorMessage = errors.length > 0 ? errors.join(', ') : 'Please check the form for errors.';
                    } else {
                        errorMessage = error.response.data.message || 'Validation error occurred.';
                    }
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.errors) {
                    // Handle field-specific errors from API
                    const fieldErrors = error.response.data.errors;
                    setValidationErrors(fieldErrors);
                    errorMessage = 'Please check the form for errors.';
                }
            }

            setApiError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to determine field column size
    const getFieldColumnSize = (field) => {
        if (['approver_levels', 'poc_mail'].includes(field)) {
            return { lg: 12, md: 12 };
        }
        return { lg: 6, md: 6, sm: 12 };
    };

    // Helper function to get field attributes based on validation rules
    const getFieldAttributes = (field) => {
        const fieldConfig = fields[field];
        const attributes = {};

        if (fieldConfig?.required) {
            attributes.required = true;
        }
        if (fieldConfig?.maxLength) {
            attributes.maxLength = fieldConfig.maxLength;
        }
        if (fieldConfig?.pattern && fieldConfig.type === 'text') {
            attributes.pattern = fieldConfig.pattern.source;
        }

        return attributes;
    };

    const handleOpenCreateModal = (fieldType) => {
        setCurrentModalType(fieldType);
        setShowCreateModal(true);
    };

    const handleCreateModalClose = () => {
        setShowCreateModal(false);
        setCurrentModalType(null);
    };

    const handleCreateItem = async (formData) => {
        if (!currentModalType || !fields[currentModalType]) return;
        setCreatingItem(true);
        try {
            const config = fields[currentModalType];
            const response = await instance.post(config.apiEndpoint, formData);
            if (response?.data?.type === "success") {
                const newCategory = response.data.data;
                setCategoryTypes(prev => [...prev, newCategory]);
                setData(prev => ({ ...prev, category: newCategory.id }));
                setTouched(prev => ({ ...prev, category: true }));
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.category;
                    return newErrors;
                });
                toast.success(response.data.message, 'Created')
            }
            handleCreateModalClose();
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setCreatingItem(false);
        }
    };
    return (
        <>
            <Offcanvas
                show={show}
                onHide={onHideFunc}
                placement="end"
                backdrop="static"
                className="w-100"
                style={{
                    maxWidth: '42rem',
                    width: '70vw'
                }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="!text-[24px]">
                        Create New Client
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body
                    className="grid-example"
                    style={{
                        overflowY: 'auto',
                        padding: '1rem'
                    }}
                >
                    <div style={{ minHeight: "400px" }}>
                        <div className="sign-user_card">
                            {/* API Success/Error Messages */}
                            {apiSuccess && (
                                <Alert variant="success" className="mb-3">
                                    {apiSuccess}
                                </Alert>
                            )}
                            {apiError && (
                                <Alert variant="danger" className="mb-3">
                                    {apiError}
                                </Alert>
                            )}

                            <Form className="login-content" onSubmit={handleFormSubmit}>
                                <Row>
                                    {!!fields && (
                                        Object.keys(fields).map((field) => {
                                            const colSize = getFieldColumnSize(field);
                                            const fieldAttributes = getFieldAttributes(field);
                                            const hasError = !!(touched[field] && allErrors[field]);

                                            return (
                                                <Col {...colSize} key={field}>
                                                    <Form.Group className="mb-3">
                                                        {fields[field]['type'] === 'text' && (
                                                            <>
                                                                <Form.Label className="fw-bold !text-base">
                                                                    {fields[field]['label']}
                                                                    {fields[field]['required'] && <span className="text-danger ms-1">*</span>}
                                                                </Form.Label>
                                                                <Form.Control
                                                                    name={fields[field]['name']}
                                                                    type={fields[field]['type']}
                                                                    placeholder={fields[field]['placeholder']}
                                                                    value={data[field] || ''}
                                                                    onChange={(e) => handleChange(e, field)}
                                                                    onBlur={() => handleBlur(field)}
                                                                    isInvalid={hasError}
                                                                    disabled={isSubmitting}
                                                                    {...fieldAttributes}
                                                                />
                                                            </>
                                                        )}

                                                        {fields[field]['type'] === 'select' && (
                                                            <>
                                                                <Form.Label className="fw-bold !text-base">
                                                                    {fields[field]['label']}
                                                                    {fields[field]['required'] && <span className="text-danger ms-1">*</span>}
                                                                </Form.Label>
                                                                <div className="d-flex">
                                                                    <Form.Select
                                                                        name={fields[field]['name']}
                                                                        value={data[field] || ''}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        onBlur={() => handleBlur(field)}
                                                                        isInvalid={hasError}
                                                                        disabled={isSubmitting}
                                                                        required={fields[field]['required']}
                                                                    >
                                                                        <option value="">Select {fields[field]['label']}</option>
                                                                        {field === 'category' ? (
                                                                            Array.isArray(categoryTypes) && categoryTypes.length > 0 ? (
                                                                                categoryTypes.map(type => (
                                                                                    <option key={type.id} value={type.id}>
                                                                                        {truncateTextOnly(type.name, 50)}
                                                                                    </option>
                                                                                ))
                                                                            ) : !loadingCategoryTypes ? (
                                                                                <option value="" disabled>
                                                                                    No categories Found
                                                                                </option>
                                                                            ) : null
                                                                        ) : (
                                                                            userData?.map((option) => (
                                                                                <option key={option.user_id} value={option.user_id}>
                                                                                    {truncateText(option.name, 30)}
                                                                                </option>
                                                                            ))
                                                                        )}
                                                                    </Form.Select>
                                                                    {field === 'category' && (
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            className='no-hover ml-2'
                                                                            onClick={() => handleOpenCreateModal('category')}
                                                                        >
                                                                            <FaPlus />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}

                                                        {fields[field]['type'] === 'checkbox' && (
                                                            <Form.Check
                                                                type="checkbox"
                                                                name={fields[field]['name']}
                                                                id={`${fields[field]['name']}-customCheck`}
                                                                label={fields[field]['label']}
                                                                checked={!!data[field]}
                                                                onChange={(e) => handleChange(e, field)}
                                                                disabled={isSubmitting}
                                                            />
                                                        )}

                                                        {fields[field]['type'] === 'checkbox_group' && (
                                                            <div className="checkbox-group left-0">
                                                                <Form.Label className="mb-4 pb-2 block !border-b !border-slate-200 !text-base !text-[20px] leading-7 !text-[#0f172a]">
                                                                    {fields[field]['label']}
                                                                    {fields[field]['required'] && <span className="text-danger ms-1">*</span>}
                                                                </Form.Label>
                                                                <Row>
                                                                    <Col className="mb-6">
                                                                        <div className="flex items-center gap-3 flex-wrap">
                                                                            {roleFields.map((role, index) => (
                                                                                <div key={role.id} className="flex flex-col">
                                                                                    <div className="flex items-center gap-2">
                                                                                        {index > 0 && (
                                                                                            <span className="text-[#F37920] !font-semibold text-2xl">→</span>
                                                                                        )}

                                                                                        <div className="flex flex-col">
                                                                                            {/* Show text label if it's not the first role and has empty options */}
                                                                                            {index > 0 &&
                                                                                                !loadingRoles[role.id] &&
                                                                                                roleOptions[role.id] &&
                                                                                                roleOptions[role.id].length === 0 ? (
                                                                                                <div
                                                                                                    className="d-flex align-items-center justify-content-center bg-light border rounded text-muted"
                                                                                                    style={{ width: '160px', height: '31px', fontSize: '12px' }}
                                                                                                >
                                                                                                    Top hierarchy reached
                                                                                                </div>
                                                                                            ) : (
                                                                                                <Form.Select
                                                                                                    style={{ width: '160px', fontSize: '14px' }}
                                                                                                    className={`w-24 text-sm shadow-sm ${role.error ? 'border-red-500' : ''}`}
                                                                                                    value={role.value}
                                                                                                    onChange={(e) => handleRoleChange(e, role.id)}
                                                                                                    isInvalid={role.error}
                                                                                                    disabled={isSubmitting || loadingRoles[role.id]}
                                                                                                    size="sm"
                                                                                                >
                                                                                                    <option value="">
                                                                                                        {loadingRoles[role.id] ? 'Loading...' : 'Select Role'}
                                                                                                    </option>
                                                                                                    {(roleOptions[role.id] || []).map((option) => (
                                                                                                        <option key={option.id} value={option.name}>
                                                                                                            {option.name}
                                                                                                        </option>
                                                                                                    ))}
                                                                                                </Form.Select>
                                                                                            )}
                                                                                        </div>

                                                                                        {index === roleFields.length - 1 && (
                                                                                            <div className="flex items-center gap-1">
                                                                                                {/* Only show add button if not at top hierarchy */}
                                                                                                {!(index > 0 &&
                                                                                                    !loadingRoles[role.id] &&
                                                                                                    roleOptions[role.id] &&
                                                                                                    roleOptions[role.id].length === 0) && (
                                                                                                        <Button
                                                                                                            variant="link"
                                                                                                            size="sm"
                                                                                                            className="text-[#F37920] hover:text-indigo-800 text-lg font-semibold p-0"
                                                                                                            onClick={() => handleAddRole(role.id)}
                                                                                                            disabled={isSubmitting}
                                                                                                            title="Add Role"
                                                                                                        >
                                                                                                            <FaPlus className="text-red-500 hover:text-red-700" />
                                                                                                        </Button>
                                                                                                    )}

                                                                                                {roleFields.length > 1 && (
                                                                                                    <Button
                                                                                                        variant="link"
                                                                                                        size="sm"
                                                                                                        className="text-red-500 hover:text-red-700 p-0"
                                                                                                        onClick={() => handleDeleteRole(role.id)}
                                                                                                        disabled={isSubmitting}
                                                                                                        title="Remove Role"
                                                                                                    >
                                                                                                        <FaTrash className="text-red-500 hover:text-red-700" />
                                                                                                    </Button>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>

                                                                                    {/* Error message positioned below the entire row */}
                                                                                    {role.error && (
                                                                                        <div className="text-red-500 text-xs mt-1   ">
                                                                                            Please select a role before adding a new one.
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        )}

                                                        {touched[field] && allErrors[field] && (
                                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                                {allErrors[field]}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                            );
                                        })
                                    )}
                                </Row>

                                <Row className="mt-4 mb-6 p-6 border-t border-slate-200">
                                    <Col xs={12}>
                                        <div className="flex justify-end gap-3 flex-wrap">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={handleCancel}
                                                disabled={isSubmitting}
                                                className="fw-bold"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={editClientPermission === false || isSubmitting}
                                                className="fw-bold"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Creating...
                                                    </>
                                                ) : (
                                                    'Create Client'
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <DynamicCreateModal
                show={showCreateModal}
                onHide={handleCreateModalClose}
                config={currentModalType ? fields[currentModalType] : null}
                onSubmit={handleCreateItem}
                isSubmitting={creatingItem}
            />
        </>
    );
}

export default CreateNewClient;